import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { buildPrivileges, getActiveEnrollmentFromLS } from './permissions';

const normalize = (value?: string | null) =>
  (value ?? '')
    .toString()
    .trim()
    .toLowerCase();

const normalizeUserType = (type?: string) =>
  (type ?? '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

export function usePrivileges(communityIdentifier?: string) {
  const { enrollment, loading } = useSelector((state: RootState) => state.programEnrollmentReducer);
  const { singleProgram } = useSelector((state: RootState) => state.programReducer);
  const { user } = useSelector((state: RootState) => state.auth);

  const effectiveEnrollment = enrollment ?? getActiveEnrollmentFromLS();

  const channelsByCommunity = useMemo(() => {
    const map: Record<string, any[]> = {};
    const communities = singleProgram?.communities || [];
    communities.forEach((community: any) => {
      const key = (community?.communityIdentifier || '').toString();
      if (!key) return;
      map[key] = Array.isArray(community?.channels) ? community.channels : [];
    });
    return map;
  }, [singleProgram?.communities]);

  const privileges = useMemo(
    () =>
      buildPrivileges(
        effectiveEnrollment ? [effectiveEnrollment as any] : [],
        {
          programId: singleProgram?._id,
          communityIdentifier,
        },
        {
          channelsByCommunity,
        }
      ),
    [effectiveEnrollment, singleProgram?._id, communityIdentifier, channelsByCommunity]
  );

  const normalizedType = normalizeUserType(user?.type);
  const effectiveRole = normalizeUserType((effectiveEnrollment as any)?.role);

  const shouldFallback = !effectiveRole;
  const fallbackPlatformAdmin = shouldFallback && normalizedType === 'admin';
  const fallbackCommunityAdmin = shouldFallback && normalizedType === 'communityadmin';

  const communityEnrollment = useMemo(() => {
    if (!communityIdentifier) return null;
    const target = normalize(communityIdentifier);
    return privileges.activeEnrollments.find((entry) => normalize(entry.communityIdentifier) === target) || null;
  }, [privileges.activeEnrollments, communityIdentifier]);

  const communityRole = communityEnrollment?.role || null;

  const communityModeratedChannels = useMemo(
    () =>
      (communityEnrollment?.moderatedChannels || []).filter((channel) => {
        if (!channel || channel.isActive === false) return false;
        if (!communityIdentifier) return true;
        return normalize(channel.communityIdentifier) === normalize(communityIdentifier);
      }),
    [communityEnrollment?.moderatedChannels, communityIdentifier]
  );

  const communityVisibleChannelIds = useMemo(
    () =>
      (communityEnrollment?.visibleChannelIds || []).map((id) => {
        const raw = (id ?? '').toString();
        if (!raw) return '';
        const parts = raw.split(':');
        return parts[parts.length - 1] || raw;
      }),
    [communityEnrollment?.visibleChannelIds]
  );

  return {
    privileges,
    enrollment,
    enrollmentLoading: loading,
    fallbackPlatformAdmin,
    fallbackCommunityAdmin,
    communityEnrollment,
    communityRole,
    communityModeratedChannels,
    communityVisibleChannelIds,
  };
}
